import { Chip, FormControlLabel, Stack, Switch } from "@mui/material";
import { useEffect, useMemo, useState } from "react";
import { BasketItemStatus } from "../../../Core/Api/Types/basket-item-status";
import { OrderProductType } from "../../../Core/Api/Types/order-product-type";
import { ConfigBaseProps } from "../../../Pages/Basket/basket";
import { UpdateItemSettingsOnBasket, UpdateItemStatusOnBasket } from "../../../Redux/Reducers/redux-slice-basket";
import { FormExists, GetFormIsValid, GetSectionIsValid } from "../../../Redux/Reducers/redux-slice-form-validation";
import { useAppDispatch, useAppSelector } from "../../../Redux/redux-store";
import { GetOrderProductReadableType } from "../../../Utils/helpers";
import ConfigHeader from "../ConfigHeader/config-header";
import ConfigSaveButton from "../ConfigSaveButton/config-save-button";
import ConfigFormIcon from "../ConfigFormIcon";
import FormSection from "../../Forms/FormSection/form-section";
import FormAutocomplete from "../../Forms/FormAutocomplete/form-autocomplete";
import { DbKdmRecipientInfos } from "../../../Core/Api/Types/db-kdm-recipient-infos";
import FormInputDate from "../../Forms/FormInputDate/form-input-date";
import GlobalConst from "../../../Core/Utils/Constants/globals";
import dayjs from "dayjs";
import { DcpTransferGlobecastProfile } from "../../../Core/Api/Types/dcp-transfer-globecast-profile";
import FormInputText from "../../Forms/FormInputText/form-input-text";
import { immerable } from "immer";
import { GetKdmRecipientLabel } from "../../../Utils/Helpers/IntelliTranskode/intellitranskode-helpers";
import ConfigCommonSectionFormScheduling from "../ConfigCommon/config-common-section-form-scheduling";
import { useTranslation } from "react-i18next";

interface ConfigDcpTransferGlobecast extends ConfigBaseProps {}

const ConfigDcpTransferGlobecast = (props: ConfigDcpTransferGlobecast) => {
  const { t } = useTranslation();
  const { handleSubmit, handleChangeProduct, handleCancel, item, ...stackProps } = props;

  const settings: DcpTransferGlobecastProfile = useMemo(
    () => item.selectedProduct?.settings,
    [item.selectedProduct?.settings]
  );

  const [searchPattern, setSearchPattern] = useState<string>("");
  const [onlyUpload, setOnlyUpload] = useState(
    !settings || settings.selectedRecipientIds === null || settings.selectedRecipientIds.length === 0
  );

  const formId = useMemo(() => `form-dcp-transfer-globecast-${item.id}`, []);
  const formValid = useAppSelector(GetFormIsValid(formId));
  const formExists = useAppSelector(FormExists(formId));
  const TransferGlobecastSectionId = useMemo(() => `${formId}-configure-section`, []);

  const dispatch = useAppDispatch();

  const isConfigureSectionValid = useAppSelector(GetSectionIsValid(formId, TransferGlobecastSectionId));

  // Update settings
  const updateSettings = (settings: DcpTransferGlobecastProfile) => {
    dispatch(UpdateItemSettingsOnBasket({ itemId: item.id, settings: settings }));
  };

  useEffect(() => {
    if (!formValid && formExists) dispatch(UpdateItemStatusOnBasket({ itemId: item.id, status: BasketItemStatus.New }));
  }, [formValid]);

  const selectAll: DbKdmRecipientInfos = {
    platformId: null,
    creationInfos: null,
    updateInfos: null,
    serialNumber: null,
    address: null,
    zipCode: null,
    city: null,
    region: null,
    country: null,
    name: "Select all",
    roomName: null,
    type: null,
    theater: null,
    projector: null,
    globecast: null,
    cert: null,
    contact: null,
    id: -1,
    isNew: false,
    [immerable]: true,
  };

  const limitTags = 10;

  return (
    <Stack width={"100%"} {...stackProps}>
      <ConfigHeader
        configType={item.selectedProduct?.type ? item.selectedProduct?.type : OrderProductType.DcpTransferGlobecast}
        title={GetOrderProductReadableType(
          item.selectedProduct?.type ? item.selectedProduct?.type : OrderProductType.DcpTransferGlobecast
        )}
        handleCancel={handleCancel}
        handleChangeProduct={handleChangeProduct}
        itemId={item.id}
      />
      <Stack spacing={1.5} width={"100%"} {...stackProps}>
        <FormSection
          icon={<ConfigFormIcon icon="meta" />}
          title={t("form:section.title.configureGlobecaseTransfer")}
          status={isConfigureSectionValid ? "complete" : "warning"}
          statusText={isConfigureSectionValid ? t("common:configured") : t("common:configurationRequired")}
        >
          <Stack spacing={2} width={"100%"} {...stackProps}>
            <FormControlLabel
              sx={{ mt: 1.5, ml: 0 }}
              control={<Switch size="small" checked={onlyUpload} onChange={(e) => setOnlyUpload(e.target.checked)} />}
              label={t("field:label.uploadOnGlobecastOnlySwitch")}
            />

            {!onlyUpload && (
              <>
                <FormAutocomplete
                  label={t("field:label.selectSites")}
                  multiple
                  validationProps={{
                    formId: formId,
                    formSectionId: TransferGlobecastSectionId,
                    fieldId: "select-recipient",
                    required: !onlyUpload,
                  }}
                  onInputChange={(e, value, reason) => {
                    if (reason === "input") setSearchPattern(value);
                  }}
                  inputValue={searchPattern}
                  onChange={(value) => {
                    const newSettings = { ...settings };
                    let recipientIds = Object.assign([], newSettings.selectedRecipientIds);

                    if (value.filter((x) => x.id === -1).length > 0 && searchPattern !== "") {
                      if (recipientIds === null) recipientIds = [];

                      settings.recipients
                        .filter((x) => GetKdmRecipientLabel(x)?.includesAllWords(searchPattern))
                        .map((val) => val.id)
                        .forEach((x) => {
                          recipientIds.push(x);
                        });

                      setSearchPattern("");
                    } else {
                      recipientIds = value.map((val) => val.id);
                    }

                    newSettings.selectedRecipientIds = recipientIds;

                    updateSettings(newSettings);
                  }}
                  options={[selectAll, ...settings.recipients]}
                  value={settings.recipients.filter((c) => settings.selectedRecipientIds?.includes(c.id)) || []}
                  getOptionLabel={(option) => GetKdmRecipientLabel(option as DbKdmRecipientInfos)}
                  filterOptions={(x, s) =>
                    x.filter((a) => a.id === -1 || GetKdmRecipientLabel(a)?.includesAllWords(searchPattern))
                  }
                  renderTags={(value, getTagProps) => {
                    const numTags = value.length;

                    return (
                      <>
                        {value.slice(0, limitTags).map((option, index) => (
                          <Chip {...getTagProps({ index })} key={index} label={GetKdmRecipientLabel(option)} />
                        ))}

                        {numTags > limitTags && ` +${numTags - limitTags}`}
                      </>
                    );
                  }}
                />
                <FormInputDate
                  label={t("field:label.broadcastingDate")}
                  format={GlobalConst.MOMENT_DATE_FORMAT_DATENOHOURS}
                  hideReset
                  validationProps={{
                    formId: formId,
                    formSectionId: TransferGlobecastSectionId,
                    fieldId: "broadcasting-date",
                    required: false,
                    initialValue: new Date(settings.diffusionDate),
                  }}
                  value={dayjs(settings.diffusionDate)}
                  onChange={(value) => {
                    const newSettings = { ...settings };
                    newSettings.diffusionDate = value.toJSON();
                    updateSettings(newSettings);
                  }}
                />
              </>
            )}

            <FormInputText
              label={t("field:label.annotationText")}
              placeholder={t("field:label.annotationText")}
              value={settings.annotationText}
              onChange={(v) => {
                const newSettings = { ...settings };
                newSettings.annotationText = v.target.value;
                updateSettings(newSettings);
              }}
              validationProps={{
                formId: formId,
                formSectionId: TransferGlobecastSectionId,
                fieldId: "annotation-text",
                initialState: false,
                required: false,
              }}
              disabled={false}
            />
            {/* <FormControlLabel
              sx={{ mt: 1.5, ml: 0 }}
              control={
                <Switch
                  size="small"
                  checked={settings?.forceDownload ?? false}
                  onChange={(e, value) => {
                    const newSettings = { ...settings };
                    newSettings.forceDownload = value;
                    updateSettings(newSettings);
                  }}
                />
              }
              label="Force download"
            /> */}
          </Stack>
        </FormSection>
        <ConfigCommonSectionFormScheduling item={item} formid={formId} />
      </Stack>
      <ConfigSaveButton item={item} onSubmit={handleSubmit} disabled={!formValid} />
    </Stack>
  );
};

export default ConfigDcpTransferGlobecast;
